
import React, { useState, useEffect, useRef, useMemo } from 'react';
import api from '../../services/api';
import * as S from './styles'
import LoadingBackground from '../LoadingBackground';
import CustomField from '../CustomField';
import Faq from '../Faq';

const CustomGroups = ({ entity, entityId, setCustomFieldsToSave, disabled }) => {
  // entity é o local, ex: 1-cliente, 2-produto, 3-primeiro passo pedido e 4-último passo pedido
  // entityId é o id do produto, cliente, pedido
  const [customGroups, setCustomGroups] = useState([])
  const [customFields, setCustomFields] = useState([])
  const [customFieldsValues, setCustomFieldsValue] = useState([])
  const [fieldValues, setFieldValues] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  // Atualiza o estado principal quando um campo é modificado
  const updateFieldValue = (fieldId, newValue) => {
    setCustomFieldsValue(prev => {
      // Garante que o prev sempre seja um array
      const prevArray = Array.isArray(prev) ? prev : Object.values(prev)
      return prevArray.map(item => {
        const findFieldWithoutValue = customFields?.find(field => field.id === item.customFieldId)
        if (item.customFieldId === fieldId) {
          let formattedValue = newValue;
          if (Array.isArray(newValue)) {
            formattedValue = newValue?.length === 0 ? [""] : newValue.map(v => v.value)
          } else if (newValue && typeof newValue === 'object') {
            formattedValue = newValue?.length === 0 ? [""] : [newValue.value]
          } else {
            formattedValue = newValue?.length === 0 ? [""] : [String(newValue)]
          }
          return { ...item, values: formattedValue }
        }
        return {
          ...item,
          availableIn: findFieldWithoutValue?.availableIn,
          requiredIn: findFieldWithoutValue?.requiredIn,
        }
      })
    })
  }

  const handleInputChange = (fieldId, value, isSelect) => {
    if (isSelect) {
      updateFieldValue(fieldId, value)
    } else {
      setFieldValues(prev => ({
        ...prev,
        [fieldId]: value || "",
      }))
    }
  }

  const handleInputBlur = (fieldId, value) => {
    updateFieldValue(fieldId, value)
  }

  const isValidValue = (value) => {
    if (Array.isArray(value)) {
      return value.length > 0 && value.some(v => v !== "");
    };
    return value !== "" && value !== "0.00" && value != null;
  };

  const renderField = (field) => {
    const findFieldWithValue = customFieldsValues?.find(item => item.customFieldId === field.id);
    const values = findFieldWithValue?.values || [];
    const fallbackValue = field.type === 5 ? values : values[0] || '';
    const currentValue = fieldValues[field.id] !== undefined ? fieldValues[field.id] : fallbackValue;
    
    return (
      (entity === 2
        ? isValidValue(currentValue)
        : true
      ) &&
        <CustomField
          key={field.id}
          entity={entity}
          field={field}
          value={currentValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          disabled={disabled}
        />
    )
  }

  const renderGroup = (group) => (
    <S.CustomGroupWrapper key={`group-${group.id}`} id={`customGroup-${group.order}`}>
      <S.CustomGroupTitleWrapper>
        <S.CustomGroupTitle>{group.name}</S.CustomGroupTitle>
      </S.CustomGroupTitleWrapper>
      <S.CustomGroupFieldsWrapper>
        {group.fields.map(renderField)}
      </S.CustomGroupFieldsWrapper>
    </S.CustomGroupWrapper>
  )

  const validGroups = useMemo(() => {
    const groupHasValidField = (group) => {
      return group.fields.some(field => {
        const fieldValueObj = customFieldsValues.find(val => val.customFieldId === field.id);
        const value = fieldValueObj?.values[0] ?? [];
        return isValidValue(value);
      });
    };

    if (entity === 2) {
      return customGroups.filter(groupHasValidField);
    } else {
      return customGroups;
    };
  }, [customGroups, customFieldsValues]);

  useEffect(() => {
    const getCustomFields = async () => {
      try {
        setIsLoading(true);
        const groupsResponse = await api.get(`api/v2/CustomGroup?Entity=${entity}`)
        const groups = (groupsResponse?.data?.response?.data || []).filter(group => group.availableIn !== 1)
        const groupsWithFields = await Promise.all(
          groups.map(async (group) => {
            try {
              const fieldsResponse = await api.get(`api/v2/CustomField?CustomGroupId=${group.id}`)
              const fields = (fieldsResponse?.data?.response?.data || []).filter(field => field.availableIn !== 1)

              return {
                ...group,
                fields: fields.sort((a, b) => a.order - b.order)
              }
            } catch (error) {
              console.error(`Erro ao carregar campos customizados do grupo ${group.id}:`, error);
              return { ...group, fields: [] };
            }
          })
        )

        const filteredGroups = groupsWithFields.filter(group => group.fields.length > 0)
        const allFields = filteredGroups.flatMap(group => group.fields)
        setCustomGroups(filteredGroups)
        setCustomFields(allFields)
      } catch (error) {
        console.error('Error fetching custom fields:', error)
      } finally {
        setIsLoading(false)
      }
    }

    if (entity) {
      getCustomFields()
    }
  }, [entity])

  useEffect(() => {
    const getCustomFieldsValues = async () => {
      if (entityId && customFields.length > 0) {
        try {
        setIsLoading(true);
          const valuesFromApi = []
          for (const field of customFields) {
            try {
              const result = await api.get(`api/v2/CustomFieldValue?EntityId=${entityId}&CustomFieldId=${field.id}`)
              const fieldData = result.data.response.data[0]
              
              if (fieldData) {
                valuesFromApi.push({
                  ...fieldData,
                  availableIn: field?.availableIn,
                  requiredIn: field?.requiredIn,
                })
              } else {
                // Cria um objeto vazio para campos sem valor no banco
                valuesFromApi.push({
                  id: 0,
                  customFieldId: field.id,
                  entityId: entityId,
                  values: field.type === 5 ? [] : [''], // Array vazio para multi-select, array com string vazia para outros
                  availableIn: field?.availableIn,
                  requiredIn: field?.requiredIn,
                })
              }
            } catch (error) {
              console.error(`Error loading value for field ${field.id}:`, error)
              valuesFromApi.push({
                id: 0,
                customFieldId: field.id,
                entityId: entityId,
                values: field.type === 5 ? [] : [''],
                availableIn: field?.availableIn,
                requiredIn: field?.requiredIn,
              })
            }
          }

          setCustomFieldsValue(valuesFromApi);
        } catch (error) {
          console.error('Error in custom fields values loading:', error)
        } finally {
        setIsLoading(false)
        }
      }
    }

    if (entityId && customFields.length > 0) {
      getCustomFieldsValues()
    } else {
      const defaultObj = customFields.map(field => ({
        id: 0,
        entityId: 0,
        customFieldId: field.id,
        values: [],
        availableIn: field?.availableIn,
        requiredIn: field?.requiredIn,
      }))
      setCustomFieldsValue(defaultObj)
    }
  }, [entityId, customFields])

  useEffect(() => {
    if (setCustomFieldsToSave) {
      setCustomFieldsToSave(customFieldsValues);
    }
  }, [customFieldsValues])

  if (isLoading) return <LoadingBackground />
  if (validGroups?.length === 0) return null;

  return (
    validGroups?.length > 0 &&
      <S.CustomGroups id={`groupEntity-${entity}`}>
        {entity === 2 
          ? <Faq productDetails title='Informações Adicionais' component={validGroups.map(renderGroup)} renderOpened/>
          : validGroups.map(renderGroup)}
      </S.CustomGroups>
  )
}

export default CustomGroups